import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Stack,
  Text,
  Title,
  Tabs,
  TextInput,
  Alert,
  Button,
  Group,
} from "@mantine/core";
import {
  IconFingerprint,
  IconLock,
  IconMail,
  IconArrowRight,
  IconAlertCircle,
} from "@tabler/icons-react";
import { usePortalAuthStore } from "../../store/portalAuth.store";
import { physicianPortalService } from "../../services/physicianPortal.service";
import { physicianPasskeyService } from "../../services/physicianPasskey.service";
import { OtpInput } from "../../components/portal/OtpInput";
import { PortalPasswordLoginForm } from "../../components/portal/PortalPasswordLoginForm";

export function PortalLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get("caseId");
  const login = usePortalAuthStore((s) => s.login);

  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  const [otpEmail, setOtpEmail] = useState("");
  const [otpStep, setOtpStep] = useState<"email" | "code">("email");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpResetKey, setOtpResetKey] = useState(0);

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    setPasskeyError(null);
    try {
      const result = await physicianPasskeyService.loginWithPasskey();
      login(result.sessionToken, result.email, result.practiceNames);
      navigate(caseId ? `/portal/report/${caseId}` : "/portal/dashboard");
    } catch (e: any) {
      setPasskeyError(e.message || "Passkey login failed");
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleOtpRequest = async () => {
    if (!otpEmail) {
      setOtpError("Email is required");
      return;
    }
    setOtpLoading(true);
    setOtpError(null);
    setOtpResetKey((k) => k + 1);
    try {
      await physicianPortalService.requestOtp(otpEmail);
      setOtpStep("code");
    } catch (e: any) {
      setOtpError(e.message || "Failed to send code");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpVerify = async (code: string) => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      const result = await physicianPortalService.verifyOtp(otpEmail, code);
      login(result.sessionToken, result.email, result.practiceNames);
      navigate(caseId ? `/portal/report/${caseId}` : "/portal/dashboard");
    } catch (e: any) {
      setOtpError(e.message || "Invalid code");
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 768px) { .portal-brand-panel { display: none !important; } }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .form-enter { animation: fadeSlideUp 0.4s ease-out forwards; }
      `}</style>

      <Box
        style={{
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Box
          className="portal-brand-panel"
          style={{
            width: "42%",
            background:
              "linear-gradient(160deg, #04119A 0%, #0d1fb5 60%, #1a2cc4 100%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "3rem",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            style={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 240,
              height: 240,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
            }}
          />
          <Box
            style={{
              position: "absolute",
              bottom: 40,
              left: -40,
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: "rgba(16,185,129,0.12)",
            }}
          />
          <Box
            style={{
              position: "absolute",
              top: "50%",
              right: 32,
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.03)",
            }}
          />

          <Stack gap="lg" style={{ position: "relative", zIndex: 1 }}>
            <Box>
              <Text
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "rgba(16,185,129,0.9)",
                  marginBottom: 12,
                }}
              >
                Physician Portal
              </Text>
              <Text
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: "2.8rem",
                  fontWeight: 400,
                  color: "#fff",
                  lineHeight: 1.1,
                }}
              >
                Gulf Coast
                <br />
                Pathologists
              </Text>
            </Box>

            <Text
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                color: "rgba(255,255,255,0.65)",
                fontSize: "1rem",
                lineHeight: 1.7,
                maxWidth: 280,
              }}
            >
              Secure access to your finalized pathology reports — available
              whenever you need them.
            </Text>

            <Box
              style={{
                marginTop: 16,
                padding: "16px 20px",
                background: "rgba(255,255,255,0.07)",
                borderRadius: 10,
                borderLeft: "3px solid rgba(16,185,129,0.6)",
                maxWidth: 280,
              }}
            >
              <Text
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 13,
                  color: "rgba(255,255,255,0.8)",
                  lineHeight: 1.6,
                }}
              >
                Sign in with your email and password, a one-time email code, or
                a passkey on devices you trust.
              </Text>
            </Box>
          </Stack>
        </Box>

        <Box
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "clamp(1rem, 5vw, 2rem)",
          }}
        >
          <Box className="form-enter" style={{ width: "100%", maxWidth: 420 }}>
            <Stack gap={6} mb="xl">
              <Text
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#10b981",
                }}
              >
                Welcome back
              </Text>
              <Title
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontWeight: 400,
                  fontSize: "2rem",
                  color: "#1e1e3f",
                  lineHeight: 1.15,
                }}
              >
                Sign In
              </Title>
              <Text
                size="sm"
                c="dimmed"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                Access your physician portal
              </Text>
            </Stack>

            <Tabs
              defaultValue="otp"
              styles={{
                tab: {
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontWeight: 500,
                  fontSize: 14,
                },
                list: { borderBottom: "2px solid #e5e7f8" },
              }}
            >
              <Tabs.List>
                <Tabs.Tab value="password" leftSection={<IconLock size={15} />}>
                  Password
                </Tabs.Tab>
                <Tabs.Tab value="otp" leftSection={<IconMail size={15} />}>
                  Email Code
                </Tabs.Tab>
                <Tabs.Tab
                  value="passkey"
                  leftSection={<IconFingerprint size={15} />}
                >
                  Use Passkey
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="password" pt="lg">
                <PortalPasswordLoginForm caseId={caseId} />
              </Tabs.Panel>

              {/* Passkey tab */}
              <Tabs.Panel value="passkey" pt="lg">
                <Stack gap="md">
                  {passkeyError && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="red"
                      variant="light"
                    >
                      {passkeyError}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    size="md"
                    leftSection={<IconFingerprint size={18} />}
                    loading={passkeyLoading}
                    onClick={handlePasskeyLogin}
                    style={{
                      background:
                        "linear-gradient(135deg, #04119A 0%, #1a2cc4 100%)",
                      border: "none",
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    Sign in with Passkey
                  </Button>
                </Stack>
              </Tabs.Panel>

              {/* OTP tab */}
              <Tabs.Panel value="otp" pt="lg">
                <Stack gap="md">
                  {otpStep === "email" ? (
                    <>
                      <TextInput
                        label="Email"
                        placeholder="doctor@example.com"
                        type="email"
                        value={otpEmail}
                        onChange={(e) => setOtpEmail(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleOtpRequest()
                        }
                        styles={{
                          label: { fontFamily: "'IBM Plex Sans', sans-serif" },
                          input: { fontFamily: "'IBM Plex Sans', sans-serif" },
                        }}
                      />

                      {otpError && (
                        <Alert
                          icon={<IconAlertCircle size={16} />}
                          color="red"
                          variant="light"
                        >
                          {otpError}
                        </Alert>
                      )}

                      <Button
                        fullWidth
                        size="md"
                        rightSection={<IconArrowRight size={17} />}
                        loading={otpLoading}
                        onClick={handleOtpRequest}
                        style={{
                          background:
                            "linear-gradient(135deg, #04119A 0%, #1a2cc4 100%)",
                          border: "none",
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        Send Code
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Step indicator */}
                      <Group gap="xs" mb={4}>
                        <Box
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "#10b981",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#fff",
                              fontFamily: "'IBM Plex Sans', sans-serif",
                            }}
                          >
                            ✓
                          </Text>
                        </Box>
                        <Text
                          size="xs"
                          c="dimmed"
                          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                        >
                          Code sent to{" "}
                          <Text
                            component="span"
                            size="xs"
                            style={{
                              color: "#1e1e3f",
                              fontWeight: 500,
                              fontFamily: "'IBM Plex Sans', sans-serif",
                            }}
                          >
                            {otpEmail}
                          </Text>
                        </Text>
                      </Group>

                      <Text
                        size="sm"
                        c="dimmed"
                        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                      >
                        Enter the 6-digit code from your email
                      </Text>

                      <OtpInput
                        key={otpResetKey}
                        onComplete={handleOtpVerify}
                        loading={otpLoading}
                        error={otpError}
                      />

                      {otpError && (
                        <Alert
                          icon={<IconAlertCircle size={16} />}
                          color="red"
                          variant="light"
                        >
                          {otpError}
                        </Alert>
                      )}

                      <Group justify="space-between" mt={4}>
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => {
                            setOtpStep("email");
                            setOtpError(null);
                          }}
                          style={{
                            fontFamily: "'IBM Plex Sans', sans-serif",
                            color: "#6b7280",
                          }}
                        >
                          Use a different email
                        </Button>
                        <Button
                          variant="subtle"
                          size="xs"
                          loading={otpLoading}
                          onClick={handleOtpRequest}
                          style={{
                            fontFamily: "'IBM Plex Sans', sans-serif",
                            color: "#04119A",
                          }}
                        >
                          Resend code
                        </Button>
                      </Group>
                    </>
                  )}
                </Stack>
              </Tabs.Panel>
            </Tabs>

            {/* Footer */}
            <Box mt="xl" pt="md" style={{ borderTop: "1px solid #e5e7f8" }}>
              <Text
                size="xs"
                c="dimmed"
                ta="center"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                Don&apos;t have access? Contact your pathology lab for an
                invitation.
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default PortalLoginPage;
